import { 
  collection, 
  query, 
  where, 
  getDocs,
  doc,
  updateDoc,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../../../index';
import { Player, TeamColor, TeamStats } from '../types';

/**
 * チームごとのスコアを集計する
 */
export const calculateTeamScores = async (sessionId: string): Promise<TeamStats[]> => {
  try {
    // すべてのプレイヤーを取得
    const playersSnapshot = await getDocs(
      collection(db, 'battleRoyale', sessionId, 'players')
    );
    
    const players = playersSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Player));

    // チームごとにスコアを集計
    const teamScoreMap: Record<TeamColor, { totalScore: number; playerCount: number }> = {
      [TeamColor.RED]: { totalScore: 0, playerCount: 0 },
      [TeamColor.BLUE]: { totalScore: 0, playerCount: 0 },
      [TeamColor.GREEN]: { totalScore: 0, playerCount: 0 },
      [TeamColor.YELLOW]: { totalScore: 0, playerCount: 0 },
      [TeamColor.PURPLE]: { totalScore: 0, playerCount: 0 },
      [TeamColor.ORANGE]: { totalScore: 0, playerCount: 0 }
    };

    players.forEach(player => {
      if (player.teamColor && teamScoreMap[player.teamColor]) {
        const score = player.answeredIds ? player.answeredIds.length : (player.score || 0);
        teamScoreMap[player.teamColor].totalScore += score;
        teamScoreMap[player.teamColor].playerCount += 1;
        console.log(`Player ${player.displayName} (${player.teamColor}): score = ${score}`);
      }
    });

    // 合計スコアを計算
    const totalScore = Object.values(teamScoreMap).reduce(
      (sum, team) => sum + team.totalScore, 
      0
    );

    // TeamStats形式に変換
    const teamStats: TeamStats[] = Object.entries(teamScoreMap).map(([color, data]) => ({
      teamColor: color as TeamColor,
      totalScore: data.totalScore,
      scorePercentage: totalScore > 0 ? (data.totalScore / totalScore) * 100 : 0,
      playerCount: data.playerCount
    }));

    console.log('Team score summary:');
    teamStats.forEach(team => {
      console.log(`${team.teamColor}: ${team.totalScore} points (${team.playerCount} players)`);
    });

    return teamStats.sort((a, b) => b.totalScore - a.totalScore);
  } catch (error) {
    console.error('Error calculating team scores:', error);
    return [];
  }
};

/**
 * セッションのチームデータを更新する
 */
export const updateSessionTeamScores = async (sessionId: string) => {
  try {
    const teamStats = await calculateTeamScores(sessionId);
    
    // セッションドキュメントのteamsフィールドを更新
    const sessionRef = doc(db, 'battleRoyale', sessionId);
    const sessionDoc = await getDocs(query(collection(db, 'battleRoyale'), where('__name__', '==', sessionId)));
    
    if (!sessionDoc.empty) {
      const currentSession = sessionDoc.docs[0].data();
      const updatedTeams = currentSession.teams.map((team: any) => {
        const stats = teamStats.find(s => s.teamColor === team.color);
        return {
          ...team,
          totalScore: stats?.totalScore || 0,
          scorePercentage: stats?.scorePercentage || 0
        };
      });

      await updateDoc(sessionRef, { teams: updatedTeams });
    }
  } catch (error) {
    console.error('Error updating team scores:', error);
  }
};

/**
 * リアルタイムでチームスコアを監視する
 */
export const watchTeamScores = (
  sessionId: string, 
  callback: (teamStats: TeamStats[]) => void
) => {
  // プレイヤーコレクションの変更を監視
  const unsubscribe = onSnapshot(
    collection(db, 'battleRoyale', sessionId, 'players'),
    async (snapshot) => {
      const teamStats = await calculateTeamScores(sessionId);
      callback(teamStats);
      
      // セッションドキュメントも更新
      await updateSessionTeamScores(sessionId);
    }
  );

  return unsubscribe;
};